import { useLayoutEffect, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { IconArrowUp } from "@tabler/icons-react";
import { useAppConfig } from "@ct-react/core";
import { LocaleLink, useLocaleContext } from "@ct-react/locale";
import { actions, form } from "../../i18n/sharable-defs";
import { useElementSize } from "../../hooks/dom";
import { useForm, validators } from "../../hooks/form";
import { useTools } from "../../hooks/tools";
import { Wrapper } from "./wrapper";
import Button from "../common/button";
import SpriteIcon from "../medias/sprite-icon";
import StickedContact from "./sticked-contact";
import Logo from "../../assets/componable-svgs/agency-logo-business.svg";
import Money from "../../assets/componable-svgs/money.svg";
import "./footer.scss";

const transDefs = defineMessages({
  alert: { id: "footer.alert-title", defaultMessage: "Restez informé" },
  alertLabel: { id: "footer.alert-label", defaultMessage: "Pour ne rien manquer de l'immobilier des 4 Vallées." },
  social: { id: "footer.social-title", defaultMessage: "Restez connecté" },
  policy: { id: "footer.policy", defaultMessage: "Politique de confidentialité" }
});

const SEND_MAIL_TO_AGENCY_GQL_MUT = gql`
  mutation MailNewsletter($agencyId: ID!, $request: MailRequestInput!) {
    mailRelatedContact(merchantId: $agencyId, request: $request) @recaptcha(action: "mail_form")
  }
`;

const RegistrationForm = () => {

  const intl = useIntl();
  const { options: { agencyId } } = useAppConfig();
  const { mutation } = useTools();

  const [ sendMail ] = useMutation(SEND_MAIL_TO_AGENCY_GQL_MUT);
  const { register, handleSubmit, formState, patch } = useForm<{ email: string }>();

  const submit = async ({ email }: { email: string }) => {
    const variables = {
      agencyId,
      request: {
        contact: { email, firstName: "indéfini", lastName: "indéfini", phone: "indéfini" },
        subject: "Inscription - newsletter",
        body: "Demande d'inscription à la newsletter"
      }
    };
    await mutation.withRecaptcha(sendMail, "mail_form")({ variables })
                  .then(() => patch(true));
  }

  return (
    <form className="registrer" onSubmit={handleSubmit(submit)}>
      <input type="email"
             {...register("email", {
               required: intl.formatMessage(form.emptyError),
               validators: { ...validators.emailFormat(intl.formatMessage(form.emailError)) }
             })}
             placeholder={intl.formatMessage(form.email)}
             autoComplete="email" />
      <Button type="submit"
              processing={formState.submit.inProcess}
              disabled={!formState.isValid}>
        {intl.formatMessage(actions.register)}
      </Button>
    </form>);

}

const Footer = () => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { ref, size } = useElementSize();

  useLayoutEffect(() => {
    !!size?.height && document.documentElement.style.setProperty("--cgv-footer-height", `${size.height}px`);
  }, [ size?.height ]);

  const policyPath = useMemo(() => `/conditions/policy-${locale.basename.toLowerCase()}.pdf`, [locale ]);

  return (
    <footer ref={ref}
            className="main-footer">

      <StickedContact />

      <Wrapper.Centered className="agency">

        <LocaleLink to="/" className="logo">
          <Logo />
        </LocaleLink>

        <div className="col contact">
          <h2>{intl.formatMessage(actions.contactUs)}</h2>
          <ul>
            <li>
              <a className="item" href="tel:+41791948392">
                <SpriteIcon hash="contact.phone" bordered={true} />
                +41 (0)79 194 83 92
              </a>
            </li>
            <li>
              <a className="item" href="https://call.whatsapp.com/voice/x4TOi7NqaSlVQy5aCtxmd5" target="_blank" rel="noopener">
                <SpriteIcon hash="social.whatsapp" bordered={true} />
                +41 (0)79 194 83 92
              </a>
            </li>
            <li>
              <a className="item" href="mailto:info@evres.ch">
                <SpriteIcon hash="contact.email" bordered={true} />
                info@evres.ch
              </a>
            </li>
            <li className="item address">
              <SpriteIcon hash="contact.pin-location" bordered={true} />
              <div className="address">
                <span>EV Real Estate & Services</span>
                <span>Chemin de Dzelon 19</span>
                <span>1997 Haute-Nendaz</span>
                <span>Valais, Suisse</span>
              </div>
            </li>
          </ul>
        </div>

        <div className="col alert">
          <h2>{intl.formatMessage(transDefs.alert)}</h2>
          <p>{intl.formatMessage(transDefs.alertLabel)}</p>
          <RegistrationForm />
        </div>

        <div className="col connect">
          <h2>{intl.formatMessage(transDefs.social)}</h2>
          <div className="socials">
            <a href="https://www.facebook.com/ElsjeVerfaillieRealEstate" target="_blank">
              <SpriteIcon hash="social.facebook" bordered={true} />
            </a>
            <a href="https://www.linkedin.com/company/ev-real-estate-and-services/" target="_blank">
              <SpriteIcon hash="social.linkedin" bordered={true} />
            </a>
            <a href="https://www.instagram.com/evrealestateservices/" target="_blank">
              <SpriteIcon hash="social.instagram" bordered={true} />
            </a>
            <a href="https://www.youtube.com/@EVRealEstateServices" target="_blank">
              <SpriteIcon hash="social.youtube" bordered={true} />
            </a>
          </div>
        </div>

        <div className="scroll-top"
             onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" })}>
          <IconArrowUp />
        </div>

        <div className="links">
          <Money />
          <span className="items">
            <a href={policyPath} target="_blank">{intl.formatMessage(transDefs.policy)}</a>
          </span>
        </div>
      </Wrapper.Centered>
    </footer>);

};

export default Footer;
